import React, { type FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import classNames from 'classnames';

import type { ReduxState } from 'app/redux/types';

import { Button } from 'app/design-system/components/Button';
import { Icon } from 'app/design-system/components/Icon';
import { useTranslation } from 'app/lang';
import { asyncQueue } from 'app/lib/asyncQueue';
import { selectSignalrConnected } from 'app/lib/signalr';
import { INTERESTING_MATCHES_TOPIC, subscribe, unsubscribe } from 'app/modules/LandingPage/InterestingMatches/actions';
import { matchIds } from 'app/modules/LandingPage/InterestingMatches/selectors/entities';

import { NoDataSkeleton } from '../NoDataSkeleton';
import { PopularMatches } from '../PopularMatches';

import dictionary from './NoData.dictionary.json';

import styles from './NoData.module.scss';

type NoData = {
  isNoAuth?: boolean;
};

export const NoData: FC<NoData> = ({ isNoAuth = false }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const signalrConnected = useSelector(selectSignalrConnected);
  const hasMatchIds = useSelector((state: ReduxState) => matchIds(state).size);
  const wrapperClassNames = classNames(styles.root, {
    [styles.centerWrapperPosition]: (isNoAuth || !hasMatchIds),
  });

  useEffect(() => {
    return () => {
      !isNoAuth && asyncQueue.removeFromQueue(
        INTERESTING_MATCHES_TOPIC,
        () => dispatch(unsubscribe()),
      );
    };
  }, [isNoAuth]);

  useEffect(() => {
    if (signalrConnected) {
      !isNoAuth && asyncQueue.addToQueue(
        INTERESTING_MATCHES_TOPIC,
        () => dispatch(subscribe()),
      );
    }
  }, [signalrConnected, isNoAuth]);

  return (
    <div className={wrapperClassNames}>
      <NoDataSkeleton />
      <h5 className={styles.title}>
        {t(dictionary.title)}
        <Icon icon="face-sad" />
      </h5>
      <p className={styles.description}>{
        isNoAuth
          ? t(dictionary.noAuthDescription)
          : t(dictionary.noDataDescription)}
      </p>

      {isNoAuth && (
        <Button component={Link}
                to="?signing=sign">
          {t(dictionary.linkText)}
        </Button>
      )}

      {Boolean(hasMatchIds && !isNoAuth) && <PopularMatches />}
    </div>
  );
};
